"use client";

import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { PopupList, useForm, useTranslation, toast, useAuth, useDocumentDrawer } from "@payloadcms/ui";
import { usePresetsConfig } from "../../usePresetsConfig.js";
import { useOpenDrawer } from "../../blocksDrawer/OpenDrawerContext.js";
import { Data } from "payload";
import { PresetBlockData } from "./types.js";
import { cleanPresetData } from "../../utils.js";

interface SaveAsPresetCoreProps {
  presetBlockData: PresetBlockData;
  rowIndex: number;
}

export function SaveAsPresetCore({ presetBlockData, rowIndex }: SaveAsPresetCoreProps) {
  const { blockType: presetType } = presetBlockData;
  const { slug, excludeKeys } = usePresetsConfig();
  const openPresetsDrawer = useOpenDrawer();
  const excludeSet = new Set(excludeKeys);

  const [DocumentDrawer, , { openDrawer, closeDrawer }] = useDocumentDrawer({
    collectionSlug: slug,
  });

  const { user } = useAuth();
  const { getData } = useForm();
  const tenantId = getData()?.tenant ?? user?.tenant?.id ?? user?.tenant ?? null;

  const { t } = useTranslation();

  const anchorRef = useRef<HTMLDivElement>(null);
  const suppressNextActionsButtonClickRef = useRef(false);
  const interceptAddBelowRef = useRef<((event: MouseEvent) => void) | null>(null);
  const [portalContainer, setPortalContainer] = useState<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!presetType) return;

    const row = anchorRef.current?.closest(".blocks-field__row");
    if (!row) return;

    const handleClick = (e: Event) => {
      const target = e.target as HTMLElement;
      const clickedButton = target.closest?.(".array-actions__button");
      if (!clickedButton) return;

      const nearestRow = clickedButton.closest(".blocks-field__row, .array-field__row");
      if (nearestRow !== row) return;

      if (suppressNextActionsButtonClickRef.current) {
        suppressNextActionsButtonClickRef.current = false;
        return;
      }

      setTimeout(() => {
        if (interceptAddBelowRef.current) {
          document.removeEventListener("click", interceptAddBelowRef.current, true);
        }

        const interceptAddBelow = (event: MouseEvent) => {
          document.removeEventListener("click", interceptAddBelow, true);
          interceptAddBelowRef.current = null;

          if (!(event.target as Element).closest?.(".array-actions__add")) {
            return;
          }

          event.preventDefault();
          event.stopPropagation();

          const actionsButton = row.querySelector<HTMLButtonElement>(".array-actions__button");

          if (actionsButton) {
            suppressNextActionsButtonClickRef.current = true;
            actionsButton.click();
          }

          openPresetsDrawer?.(rowIndex + 1);
        };

        interceptAddBelowRef.current = interceptAddBelow;
        document.addEventListener("click", interceptAddBelow, true);

        const list = document.body.querySelector(".popup__content .popup-button-list");

        if (list) {
          const existing = list.querySelector("[data-save-as-preset-container]") as HTMLDivElement | null;
          if (existing) {
            setPortalContainer(existing);
          } else {
            const container = document.createElement("div");
            container.setAttribute("data-save-as-preset-container", "true");
            list.insertBefore(container, list.firstChild);
            setPortalContainer(container);
          }
        }
      }, 0);
    };

    row.addEventListener("click", handleClick);

    return () => {
      row.removeEventListener("click", handleClick);

      if (interceptAddBelowRef.current) {
        document.removeEventListener("click", interceptAddBelowRef.current, true);
        interceptAddBelowRef.current = null;
      }
    };
  }, [openPresetsDrawer, presetType, rowIndex]);

  const blockContent = cleanPresetData(presetBlockData ?? {}, excludeSet) as Record<string, unknown>;

  const data = presetType
    ? ({
        name: `${presetType.charAt(0).toUpperCase() + presetType.slice(1)} ${new Date().toLocaleDateString()}`,
        presetBlock: [{ blockType: presetType, ...blockContent }],
        tenant: tenantId,
      } as Data)
    : undefined;

  return (
    <>
      <div ref={anchorRef} style={{ display: "none" }} />

      {presetType &&
        portalContainer &&
        createPortal(
          <div>
            <PopupList.Button className="popup-button-list__button array-actions__action" onClick={openDrawer}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="17"
                height="17"
                viewBox="0 0 20 20"
                fill="none"
                stroke="currentColor"
                strokeWidth="1"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="icon"
              >
                <path d="M12.667 2.5a1.667 1.667 0 0 1 1.167.5l3.167 3.167a1.667 1.667 0 0 1 .5 1.167V15.833a1.667 1.667 0 0 1-1.667 1.667H4.167a1.667 1.667 0 0 1-1.667-1.667V4.167a1.667 1.667 0 0 1 1.667-1.667z" />
                <path d="M14.167 17.5v-5.833a.833 .833 0 0 0-.833-.833H6.667a.833 .833 0 0 0-.833.833v5.833" />
                <path d="M5.833 2.5v3.333a.833 .833 0 0 0 .833.833h5.833" />
              </svg>

              {t("presetsPlugin:presetActions:saveButton" as never)}
            </PopupList.Button>
          </div>,
          portalContainer
        )}

      <DocumentDrawer
        initialData={data}
        onSave={({ doc }) => {
          const document = doc as Data;

          toast.success(
            t("presetsPlugin:presetActions:successSaved" as never, {
              name: document.name,
            })
          );
          closeDrawer();
        }}
      />
    </>
  );
}
